import { useEffect, useState } from "react";
import api from "../lib/api";
import { eur, itDate } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { CheckCircle2, Trash2, Repeat } from "lucide-react";

// occ: {series_id,date,description,amount,beneficiary,recurrence,paid} OR null for "create"
export default function DeadlineDialog({ open, occ, defaultDate, canEdit, onClose, onChanged }) {
  const isNew = !occ;
  const [form, setForm] = useState({ recurrence: "monthly" });
  const [scopeAsk, setScopeAsk] = useState(null); // {action:'edit'|'delete'}

  useEffect(() => {
    if (open) {
      setScopeAsk(null);
      setForm(occ
        ? { description: occ.description, amount: occ.amount, beneficiary: occ.beneficiary, start_date: occ.date, recurrence: occ.recurrence }
        : { recurrence: "monthly", start_date: defaultDate || "" });
    }
  }, [open, occ, defaultDate]);

  const create = async () => {
    if (!form.start_date) return toast.error("Inserisci la data di scadenza");
    try {
      await api.post("/deadlines", { description: form.description, amount: Number(form.amount || 0), beneficiary: form.beneficiary, start_date: form.start_date, recurrence: form.recurrence });
      toast.success("Scadenza creata"); onChanged && onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
  };

  const togglePaid = async () => {
    try {
      await api.post(`/deadlines/${occ.series_id}/pay`, { date: occ.date, paid: !occ.paid });
      toast.success(!occ.paid ? "Segnata come pagata" : "Segnata come da pagare");
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const doScope = async (scope) => {
    try {
      if (scopeAsk.action === "delete") {
        await api.delete(`/deadlines/${occ.series_id}?scope=${scope}&date=${occ.date}`);
        toast.success("Scadenza eliminata");
      } else {
        await api.put(`/deadlines/${occ.series_id}`, { scope, date: occ.date, description: form.description, amount: Number(form.amount || 0), beneficiary: form.beneficiary });
        toast.success("Scadenza aggiornata");
      }
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const askOrRun = (action) => {
    if (occ.recurrence === "monthly") setScopeAsk({ action });
    else doScopeDirect(action);
  };
  const doScopeDirect = async (action) => {
    try {
      if (action === "delete") { await api.delete(`/deadlines/${occ.series_id}?scope=all`); toast.success("Scadenza eliminata"); }
      else { await api.put(`/deadlines/${occ.series_id}`, { scope: "all", description: form.description, amount: Number(form.amount || 0), beneficiary: form.beneficiary }); toast.success("Scadenza aggiornata"); }
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="deadline-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2">
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: "#16A34A" }} />
          {isNew ? "Nuova scadenza da pagare" : "Scadenza"} {occ?.recurrence === "monthly" && <Repeat size={15} className="text-muted-foreground" />}
        </DialogTitle></DialogHeader>

        {scopeAsk ? (
          <div className="space-y-3">
            <p className="text-sm">{scopeAsk.action === "delete" ? "Vuoi eliminare…" : "Applica la modifica a…"}</p>
            <Button className="w-full" variant="outline" data-testid="scope-one" onClick={() => doScope("one")}>Solo questa scadenza ({itDate(occ.date)})</Button>
            <Button className="w-full" variant="outline" data-testid="scope-following" onClick={() => doScope("following")}>Questa e le successive</Button>
            <Button className="w-full" variant="outline" data-testid="scope-all" onClick={() => doScope("all")}>Tutta la serie</Button>
            <Button className="w-full" variant="ghost" onClick={() => setScopeAsk(null)}>Annulla</Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div><Label>Descrizione</Label><Input data-testid="dl-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="es. Rata furgone" disabled={!canEdit} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Importo €</Label><Input data-testid="dl-amount" type="number" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} disabled={!canEdit} /></div>
                <div><Label>Beneficiario</Label><Input data-testid="dl-beneficiary" value={form.beneficiary || ""} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} placeholder="es. Banca / Fornitore" disabled={!canEdit} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{isNew ? "Prima scadenza" : "Data"}</Label><Input data-testid="dl-date" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} disabled={!isNew} /></div>
                <div><Label>Ripetizione</Label>
                  <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })} disabled={!isNew}>
                    <SelectTrigger data-testid="dl-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                      <SelectItem value="none">Una tantum</SelectItem>
                      <SelectItem value="monthly">Mensile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!isNew && <p className="text-xs text-muted-foreground">Stato: {occ.paid ? <span className="text-emerald-600 font-semibold">Pagata il {itDate(occ.paid_at)}</span> : <span className="text-brand font-semibold">Da pagare</span>} · {eur(occ.amount)}</p>}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {isNew ? (
                <Button className="bg-brand text-white w-full" data-testid="dl-create" onClick={create}>Crea scadenza</Button>
              ) : (
                <div className="flex flex-wrap gap-2 w-full">
                  {canEdit && <Button data-testid="dl-pay" onClick={togglePaid} className={occ.paid ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"} variant={occ.paid ? "outline" : "default"}><CheckCircle2 size={15} className="mr-1" /> {occ.paid ? "Annulla pagamento" : "Segna pagata"}</Button>}
                  {canEdit && <Button variant="outline" data-testid="dl-save" onClick={() => askOrRun("edit")}>Salva modifiche</Button>}
                  {canEdit && <Button variant="outline" className="text-destructive border-destructive/40" data-testid="dl-delete" onClick={() => askOrRun("delete")}><Trash2 size={15} /></Button>}
                </div>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
